import { v4 as uuidv4 } from 'uuid';
import { decodeJwt } from './util';

export const presentationStates = {
  LAUNCHED: 'OpenForRecord',
  RECORDING: 'Recording',
  RECORDED: 'Recorded',
};

const LOCAL_STORAGE_TOKEN = 'MediasiteST';
export const LOCAL_STORAGE_SITE_ID = 'MediasiteSI';
export const LOCAL_STORAGE_SITE_NAME = 'MediasiteSN';
export const PAUSE_REFRESH_NEEDED_MILLISECONDS = 4 * 60 * 1000;

export const changePresentationState = async(url, sfApiKey, token, state) => {
  try {
    await fetch(url, {
      method: 'PATCH',
      headers: {
        sfapikey: sfApiKey,
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        Status: state,
      }),
    });
  } catch (error) {
    console.log(`changePresentationState encountered error: ${error.message}`);
  }
};

export const fetchMediasiteProperties = async(
  url,
  sfApiKey,
  token,
  presentationId,
  presentationUrl,
  settingsManagerObj
) => {
  var url1 = null;
  var url2 = null;
  var url3 = null;

  try {
    const response = await fetch(presentationUrl, {
      method: 'GET',
      headers: {
        sfapikey: sfApiKey,
        Authorization: `Bearer ${token}`,
      },
    });
    const responseJson =
      response.headers.get('content-type')?.indexOf('application/json') !== -1
        ? await response.json()
        : {};

    if (responseJson['#Upload'] === undefined ||
    responseJson['#Upload']?.target === undefined) {
      url1 = `${url}/FileServer/Presentation/${presentationId}`;
    } else {
      url1 = responseJson['#Upload'].target;
    }

    if (responseJson['#CreateMediaUploadUrl'] === undefined ||
    responseJson['#CreateMediaUploadUrl']?.target === undefined) {
      url2 = `${url}/Api/v1/Presentations('${presentationId}')/CreateMediaUpload`;
    } else {
      url2 = responseJson['#CreateMediaUploadUrl'].target;
    }

    if (responseJson['#CreatePendingFileDeletes'] === undefined ||
      responseJson['#CreatePendingFileDeletes']?.target === undefined) {
      url3 = `${url}/Api/v1/Presentations('${presentationId}')/CreatePendingFileDeletes`;
    } else {
      url3 = responseJson['#CreatePendingFileDeletes'].target;
    }
  } catch (error) {
    console.log(`fetchMediasiteProperties encountered error: ${error.message}`);
    settingsManagerObj.errorConditions.isMediasiteCommunicationSuccessful = false;
    settingsManagerObj.errorConditions.translate = true;

    if (
      error.message.includes('Failed to fetch') ||
      error.message.includes('NetworkError when attempting to fetch resource')
    ) {
      settingsManagerObj.errorConditions.errorMessage = 'cors-error-message';
    } else {
      settingsManagerObj.errorConditions.errorMessage = 'general-error-message';
    }
  }

  return [url1, url2, url3];
};

export const removeSessionToken = async() => {
  try {
    window.localStorage.removeItem(LOCAL_STORAGE_TOKEN);
  } catch (error) {
    console.log(`removeSessionToken encountered error: ${error.message}`);
  }
};

export const isSessionTokenExpired = sessionToken => {
  if(sessionToken === undefined || sessionToken === '' ||
    sessionToken === null || sessionToken === 'null' ||
    sessionToken === 'NULL') {
    return true;
  }

  var decodedToken = decodeJwt(sessionToken);
  var now = Math.floor(new Date().getTime() / 1000);
  var nbf = decodedToken.payload.nbf;
  var exp = decodedToken.payload.exp;
  return typeof nbf !== 'number' || typeof exp !== 'number' || now < nbf || now > exp;
};

export const getSessionToken = async(url, sfApiKey, launchToken, presentationId,
  settingsManagerObj) => {
  var resp;
  var sessionToken;
  var siteId;
  var siteName;
  var isNewTokenNeeded = false;
  var decodedToken;

  try {
    sessionToken = window.localStorage.getItem(LOCAL_STORAGE_TOKEN);
    siteId = window.localStorage.getItem(LOCAL_STORAGE_SITE_ID);
    siteName = window.localStorage.getItem(LOCAL_STORAGE_SITE_NAME);
    isNewTokenNeeded = isSessionTokenExpired(sessionToken) === true;

    if(isNewTokenNeeded) {
      resp = await exchangeLaunchToken(url, sfApiKey, launchToken);
      sessionToken = resp.SessionToken;
      decodedToken = decodeJwt(sessionToken);

      if(decodedToken !== null) {
        siteId = decodedToken.payload.siteid;
        siteName = decodedToken.payload.sitename;
      }
    }

    isNewTokenNeeded = (await isTokenValid(url, sfApiKey, sessionToken, presentationId)) === false;

    if(isNewTokenNeeded) {
      resp = await exchangeLaunchToken(url, sfApiKey, launchToken);
      sessionToken = resp.SessionToken;
      decodedToken = decodeJwt(sessionToken);

      if(decodedToken !== null) {
        siteId = decodedToken.payload.siteid;
        siteName = decodedToken.payload.sitename;
      }
    }

    window.localStorage.setItem(LOCAL_STORAGE_TOKEN, sessionToken);
    window.localStorage.setItem(LOCAL_STORAGE_SITE_ID, siteId);
    window.localStorage.setItem(LOCAL_STORAGE_SITE_NAME, siteName);
  } catch (error) {
    console.log(`getSessionToken encountered error: ${error.message}`);

    if (
      error.message.includes('Failed to fetch') ||
      error.message.includes('NetworkError when attempting to fetch resource')
    ) {
      settingsManagerObj.errorConditions.errorMessage = 'cors-error-message';
    } else {
      settingsManagerObj.errorConditions.errorMessage = 'general-error-message';
    }
  }

  return sessionToken;
};

const exchangeLaunchToken = async(url, sfApiKey, launchToken) => {
  try {
    const response = await fetch(`${url}/api/v1/Home/ExchangeLaunchToken`, {
      method: 'POST',
      body: JSON.stringify({
        LaunchToken: launchToken,
      }),
      headers: {
        sfapikey: sfApiKey,
        'Content-Type': 'application/json',
      },
    });

    const responseJson =
      response.headers.get('content-type')?.indexOf('application/json') !== -1
        ? await response.json() : {};
    const sessionToken = responseJson.SessionToken;
    const siteId = responseJson.siteid === undefined ? '' : responseJson.siteid;
    const siteName = responseJson.sitename === undefined ? '' : responseJson.sitename;

    return {
      SessionToken: sessionToken,
      SiteId: siteId,
      SiteName: siteName
    };
  } catch (error) {
    console.log(`exchangeLaunchToken encountered error: ${error.message}`);
    throw error;
  }
};

const isTokenValid = async(url, sfApiKey, sessionToken, presentationId) => {
  var testUrl = `${url}/Api/v1/Presentations('${presentationId}')`;

  try {
    const response = await fetch(testUrl, {
      method: 'GET',
      headers: {
        sfapikey: sfApiKey,
        Authorization: `Bearer ${sessionToken}`,
      },
    });

    return response.status !== 401;
  } catch (error) {
    console.log(`isTokenValid encountered an error: ${error}`);
    return false;
  }
};

export const generateNewRecordingName = deviceType => {
  const guid = uuidv4();
  const prefix = deviceType === 'desktop' ? 'display.webm' : 'camera.webm';
  return `${prefix}_${guid}.webm`;
};

export const getUpidFromToken = token => {
  var ret = decodeJwt(token);

  if(ret === null || ret.payload === null || ret.payload.upid === null) {
    return ret;
  }

  var upid = ret.payload.upid;

  if(upid.includes('-') === false) {
    upid = upid.substring(0, upid.length - 2);
    // The next line looks dumb. Blame eslint's line length constraint of 120 characters.
    upid = `${upid.substring(0, 8)}-${upid.substring(8, 12)}-${upid.substring(12, 16)}-${upid.substring(16, 20)}-` +
      `${upid.substring(20)}`;
  }

  return upid;
};

export const getLastByte = async(url, sfApiKey, sessionToken) => {
  var lastBytes = {
    byte: null,
    index: 0,
    length: 0
  };

  try {
    const sizeResponse = await fetch(url, {
      method: 'PROPFIND',
      body: '<?xml version="1.0" encoding="UTF-8"?><d:propfind xmlns:d="DAV:">' +
        '<d:prop><d:resourcetype/></d:prop></d:propfind>',
      headers: {
        Depth: 0,
        'Content-Type': 'text/xml',
        sfapikey: sfApiKey,
        Authorization: `Bearer ${sessionToken}`
      }
    });

    if (sizeResponse.status === 207) {
      const xmlString = await sizeResponse.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlString, 'application/xml');
      lastBytes.length = parseInt(xmlDoc.getElementsByTagName('d:getcontentlength')[0].textContent);
      if (lastBytes.length === 0) {
        lastBytes.byte = null;
        return lastBytes;
      }
      lastBytes.index = Math.max(0, lastBytes.length - 10); // Prevent negative index
      console.log(lastBytes.length);
      console.log(lastBytes.index);
    }

    const dataResponse = await fetch(url, {
      method: 'GET',
      headers: {
        Range: `bytes=${lastBytes.index}-${lastBytes.length - 1}`,
        sfapikey: sfApiKey,
        Authorization: `Bearer ${sessionToken}`
      }
    });

    if(dataResponse.status === 206) {
      lastBytes.byte = await dataResponse.blob();
    }
  } catch (error) {
    console.log(`getLastByte encountered an error: ${error}`);
    throw error;
  }

  return lastBytes;
};

export const setLastByte = async(url, sfApiKey, sessionToken, byteData) => {
  console.log({
    method: 'POST',
    body: byteData.byte,
    headers: {
      'Content-Range': `bytes ${byteData.index}-${byteData.length - 1}/*`,
      'Content-Type': 'video/mp4',
      sfapikey: sfApiKey,
      Authorization: `Bearer ${sessionToken}`
    }});
  try {
    const response = await fetch(url, {
      method: 'POST',
      body: byteData.byte,
      headers: {
        'Content-Range': `bytes ${byteData.index}-${byteData.length - 1}/*`,
        'Content-Type': 'video/mp4',
        sfapikey: sfApiKey,
        Authorization: `Bearer ${sessionToken}`
      }
    });

    return response.status === 201;
  } catch (error) {
    console.log(`setLastByte encountered an error: ${error}`);
    throw error;
  }
};
